/**
 * Secure storage for vault credentials using biometric authentication
 *
 * The master password is encrypted using a key derived from WebAuthn authenticatorData.
 * The authenticatorData is only available after successful biometric verification,
 * so the password can only be decrypted with a valid biometric.
 *
 * Storage format in localStorage:
 * {
 *   credentialId: string,    // WebAuthn credential ID
 *   email: string,           // Vault email
 *   encryptedPassword: string, // AES-GCM encrypted master password (base64)
 *   iv: string,              // AES-GCM IV (base64)
 *   salt: string,            // PBKDF2 salt (base64)
 * }
 */

const STORAGE_KEY = 'vault_biometric_data';

export interface BiometricStorageData {
  credentialId: string;
  email: string;
  encryptedPassword: string;
  iv: string;
  salt: string;
}

export interface RetrievedCredentials {
  email: string;
  password: string;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Derive an AES-GCM key from authenticatorData using PBKDF2
 *
 * @param authData - authenticatorData from WebAuthn assertion
 * @param salt - Random salt for PBKDF2
 * @returns CryptoKey for AES-GCM encryption/decryption
 */
async function deriveKeyFromAuthData(
  authData: ArrayBuffer,
  salt: ArrayBuffer
): Promise<CryptoKey> {
  // Import authenticatorData as key material for PBKDF2
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    authData,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  // Derive AES-256-GCM key
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Save vault credentials encrypted with biometric-derived key
 *
 * This should be called after:
 * 1. User successfully logs into vault with email + master password
 * 2. User agrees to enable biometric unlock
 * 3. createBiometricCredential() is called
 * 4. authenticateWithBiometric() returns authenticatorData
 *
 * @param credentialId - WebAuthn credential ID from createBiometricCredential
 * @param email - Vault email
 * @param masterPassword - Vault master password (will be encrypted)
 * @param authData - authenticatorData from authenticateWithBiometric
 */
export async function saveBiometricData(
  credentialId: string,
  email: string,
  masterPassword: string,
  authData: ArrayBuffer
): Promise<void> {
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes for AES-GCM

  // Derive key from authenticatorData
  const key = await deriveKeyFromAuthData(authData, salt.buffer as ArrayBuffer);

  // Encrypt master password
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(masterPassword)
  );

  // Store in localStorage
  const data: BiometricStorageData = {
    credentialId,
    email,
    encryptedPassword: arrayBufferToBase64(encrypted),
    iv: arrayBufferToBase64(iv.buffer as ArrayBuffer),
    salt: arrayBufferToBase64(salt.buffer as ArrayBuffer),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * Retrieve and decrypt vault credentials using biometric
 *
 * @param authData - authenticatorData from authenticateWithBiometric
 * @returns Decrypted email and password, or null if decryption fails
 */
export async function retrievePassword(
  authData: ArrayBuffer
): Promise<RetrievedCredentials | null> {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const data: BiometricStorageData = JSON.parse(stored);

    // Derive the same key using stored salt
    const salt = base64ToArrayBuffer(data.salt);
    const key = await deriveKeyFromAuthData(authData, salt);

    // Decrypt password
    const iv = base64ToArrayBuffer(data.iv);
    const encryptedPassword = base64ToArrayBuffer(data.encryptedPassword);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedPassword
    );

    return {
      email: data.email,
      password: new TextDecoder().decode(decrypted),
    };
  } catch (error) {
    // Decryption failed - wrong authenticator or tampered data
    console.error('Failed to decrypt biometric data:', error);
    return null;
  }
}

/**
 * Check if biometric data is configured
 */
export function hasBiometricData(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Get the stored credential ID for authentication
 */
export function getCredentialId(): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const data: BiometricStorageData = JSON.parse(stored);
    return data.credentialId;
  } catch {
    return null;
  }
}

/**
 * Get the stored email (for display on unlock screen)
 */
export function getStoredEmail(): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const data: BiometricStorageData = JSON.parse(stored);
    return data.email;
  } catch {
    return null;
  }
}

/**
 * Clear biometric data (on logout or when disabling biometric unlock)
 */
export function clearBiometricData(): void {
  localStorage.removeItem(STORAGE_KEY);
}
